import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState(null);
  const [success, setSuccess] = useState(false);
  const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const connectionAttemptedRef = useRef(false);

  const forgotPassword = useCallback(async ({ email }) => {
    if (!mountedRef.current) return;

    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Ensure WebSocket is connected
      if (!isConnected) {
        if (!connectionAttemptedRef.current) {
          connectionAttemptedRef.current = true;
          await connect();
        }

        await new Promise((resolve, reject) => {
          const start = Date.now();
          const interval = setInterval(() => {
            if (!mountedRef.current) {
              clearInterval(interval);
              reject(new Error('Component unmounted'));
            } else if (isOpen()) {
              clearInterval(interval);
              resolve(true);
            } else if (Date.now() - start > 10000) {
              clearInterval(interval);
              reject(new Error('Connection timeout'));
            }
          }, 100);
        });
      }

      // Send forgot password request
      const response = await send('cms_send_email_otp_cms_user', { email });
      console.log('Forgot Password response:', response);

      if (!response || response.type !== 'cms_send_email_otp_cms_user_response') {
        throw new Error('Invalid response from server');
      }

      if (!response.success) {
        throw new Error(response.message || 'Request failed');
      }

      // Save email in state & localStorage
      setEmail(email);
      localStorage.setItem('forgot_password_email', email);

      setSuccess(true);
      return { success: true, email };
    } catch (err) {
      setError(err.message);
      setEmail(null);
      setSuccess(false);
      return { success: false, error: err.message };
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isConnected, isOpen, connect, send]);

  // Example event hook (if server pushes forgot-password updates)
  useEffect(() => {
    const unregister = registerHandler('forgot_password_update', (msg) => {
      if (mountedRef.current) {
        console.log('Forgot password update:', msg);
      }
    });
    return () => unregister();
  }, [registerHandler]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false };
  }, []);

  return { email, isLoading, error, success, forgotPassword };
}
